import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import type { WelcomeMessage } from './useWelcomeMessages';

export interface WelcomeMessageWithAnalytics extends WelcomeMessage {
  views_count: number;
  dismissals_count: number;
}

export interface CreateWelcomeMessageInput {
  title: string;
  body: string;
  target_audience: 'vendors' | 'clients' | 'both';
  style: 'info' | 'success' | 'warning' | 'important';
  start_date: string;
  end_date: string | null;
  display_mode: 'always' | 'dismissable';
  priority: number;
  status: 'draft' | 'active' | 'archived';
}

export function useAdminWelcomeMessages() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all welcome messages with analytics
  const { data: messages = [], isLoading, error } = useQuery({
    queryKey: ['admin-welcome-messages'],
    queryFn: async () => {
      // First, fetch all welcome messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('welcome_messages')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (messagesError) throw messagesError;

      // Then fetch analytics counts for each message
      const messagesWithAnalytics: WelcomeMessageWithAnalytics[] = await Promise.all(
        (messagesData || []).map(async (msg) => {
          const { count: viewsCount } = await supabase
            .from('welcome_message_analytics')
            .select('*', { count: 'exact', head: true })
            .eq('message_id', msg.id)
            .eq('event_type', 'view');

          const { count: dismissalsCount } = await supabase
            .from('welcome_message_analytics')
            .select('*', { count: 'exact', head: true })
            .eq('message_id', msg.id)
            .eq('event_type', 'dismiss');

          return {
            ...msg,
            views_count: viewsCount || 0,
            dismissals_count: dismissalsCount || 0,
          } as WelcomeMessageWithAnalytics;
        })
      );

      return messagesWithAnalytics;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (input: CreateWelcomeMessageInput) => {
      const { data, error } = await supabase
        .from('welcome_messages')
        .insert({
          ...input,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-welcome-messages'] });
      toast.success('Welcome message created successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to create welcome message', {
        description: error.message,
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: CreateWelcomeMessageInput & { id: string }) => {
      const { data, error } = await supabase
        .from('welcome_messages')
        .update({
          ...input,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-welcome-messages'] });
      toast.success('Welcome message updated successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to update welcome message', {
        description: error.message,
      });
    },
  });

  // Archive mutation (soft delete)
  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('welcome_messages')
        .update({
          status: 'archived',
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-welcome-messages'] });
      toast.success('Welcome message archived');
    },
    onError: (error: Error) => {
      toast.error('Failed to archive welcome message', {
        description: error.message,
      });
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async (message: WelcomeMessage) => {
      const { data, error } = await supabase
        .from('welcome_messages')
        .insert({
          title: `${message.title} (Copy)`,
          body: message.body,
          target_audience: message.target_audience,
          style: message.style,
          start_date: new Date().toISOString(),
          end_date: message.end_date,
          display_mode: message.display_mode,
          priority: message.priority,
          status: 'draft',
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-welcome-messages'] });
      toast.success('Welcome message duplicated');
    },
    onError: (error: Error) => {
      toast.error('Failed to duplicate welcome message', {
        description: error.message,
      });
    },
  });

  return {
    messages,
    isLoading,
    error,
    createMessage: createMutation.mutateAsync,
    updateMessage: updateMutation.mutateAsync,
    archiveMessage: archiveMutation.mutateAsync,
    duplicateMessage: duplicateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isArchiving: archiveMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
  };
}
